import { defaultLanguage } from 'cf-i18n/constants'
import { useLangCookie } from 'cf-i18n/cookies'
import * as Dialog from 'primitives/dialog'
import { IconButton } from 'primitives/buttons/icon-button'
import { XCloseIcon } from 'primitives/icons'
import { PaymentStatus } from '@repo/graphql-types'
import { UpgradeCompleteScreenDialog } from '../../components/upgrade-complete-screen-dialog'
import i18n from '../i18n/i18n.json'
import { toSupportedLanguage } from '../../i18n/supported-language'
import { Z_INDEX_MODALS } from 'constants/modals'

export interface StatusDialogsResult {
  error: { message: string; type: string } | null
  message?: string | null
  paymentsStatus?: PaymentStatus
  userBanned?: boolean | null
}

export interface ActivateTrialStatusDialogsProps {
  onErrorClose: () => void
  onSuccessOpenChange: (open: boolean) => void
  result: StatusDialogsResult | null
  subscriptionType: 'all-access' | 'studio-ai'
  /**
   * Optional z-index for the rendered dialogs. Defaults to
   * `Z_INDEX_MODALS.oneClickUpgrade` (modal `10000`, success `10002`).
   *
   * Override this when the host renders the dialog on top of another modal
   * whose z-index is higher than the default — e.g. the PDP Studio
   * Customizer's `CoinTopUpDialog` sits at `Z_INDEX_MODALS.payment.wp`
   * (`10003`), so the remediation dialog must be at least `10004` to stack
   * on top of it (STU-7698).
   */
  zIndex?: number
  /**
   * Optional portal target. Forwarded to `Dialog.Portal`. Defaults to
   * `document.body`. Used by hosts that scope their UI inside an isolated
   * portal (e.g. the WordPress PDP widget's `data-cf-external` container).
   */
  portalContainer?: HTMLElement | null
}

export function ActivateTrialStatusDialogs({
  onErrorClose,
  onSuccessOpenChange,
  result,
  subscriptionType,
  zIndex,
  portalContainer,
}: ActivateTrialStatusDialogsProps) {
  const [langCookie] = useLangCookie()
  const locale = langCookie || defaultLanguage
  const t = i18n[toSupportedLanguage(locale)]

  const isErrorOpen = Boolean(
    (result && result.paymentsStatus !== PaymentStatus.Succeeded) || result?.userBanned,
  )

  const errorZIndex = zIndex ?? Z_INDEX_MODALS.oneClickUpgrade.modal
  const successZIndex = zIndex ?? Z_INDEX_MODALS.oneClickUpgrade.success

  return (
    <>
      <Dialog.Root
        open={isErrorOpen}
        onOpenChange={open => {
          if (!open) {
            onErrorClose()
          }
        }}
        defaultOpen={false}
      >
        <Dialog.Portal container={portalContainer ?? undefined}>
          <div data-cf-external>
            <Dialog.Body dismissable={false} zIndex={errorZIndex} overlayZIndex={errorZIndex - 1}>
              <div className="font-poppins relative flex w-full max-w-[450px] flex-col items-center rounded bg-[#ffffff] p-5 text-[#393D56] shadow-md">
                {result?.error ? (
                  <h3 className="text-center text-[16px] font-bold">{t.messages.upgrade_failed}</h3>
                ) : null}
                {result?.userBanned ? (
                  <h3 className="text-center text-[16px] font-bold">{t.messages.user_banned}</h3>
                ) : null}
                {result?.paymentsStatus === PaymentStatus.Processing ? (
                  <h3 className="text-center text-[16px] font-bold">
                    {t.messages.processing}&nbsp;
                    {result.message ? (
                      <a
                        className="underline"
                        target="_blank"
                        href={result.message}
                        rel="noreferrer"
                      >
                        {t.messages.here}
                      </a>
                    ) : null}
                  </h3>
                ) : null}
                {result?.paymentsStatus === PaymentStatus.RequiresAction ? (
                  <h3 className="flex flex-col text-center text-[16px] font-bold">
                    {result.message ? t.messages.requires_action : t.messages.upgrade_failed}
                    {result.message ? (
                      <a
                        className="underline"
                        target="_blank"
                        href={result.message}
                        rel="noreferrer"
                      >
                        {t.messages.cta}
                      </a>
                    ) : null}
                  </h3>
                ) : null}
                {result?.paymentsStatus === PaymentStatus.Failed && !result?.userBanned ? (
                  <h3 className="flex flex-col text-center text-[16px] font-bold">
                    {result.message ? (
                      <>
                        {t.messages.upgrade_failed_1}
                        <a
                          className="underline"
                          target="_blank"
                          href={result.message}
                          rel="noreferrer"
                        >
                          {t.messages.cta}
                        </a>
                      </>
                    ) : (
                      <>
                        {t.messages.invalid_payment}
                        <a
                          className="underline"
                          target="_blank"
                          href="/my-account/payment-methods/"
                          rel="noreferrer"
                        >
                          {t.messages.click_here}
                        </a>
                      </>
                    )}
                  </h3>
                ) : null}
                <div className="absolute right-[-18px] top-[-18px] [@media(min-width:1340px)]:-top-[52px] [@media(min-width:1340px)]:translate-y-full">
                  <Dialog.Close asChild>
                    <IconButton
                      variant="violet"
                      className="h-[36px] w-[36px] rounded-full transition-transform hover:scale-110 active:scale-100"
                    >
                      <img
                        src={XCloseIcon.src}
                        width="30"
                        height="30"
                        className="h-[30px] w-[30px] [filter:invert(1)]"
                      />
                    </IconButton>
                  </Dialog.Close>
                </div>
              </div>
            </Dialog.Body>
          </div>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root
        open={result?.paymentsStatus === PaymentStatus.Succeeded}
        onOpenChange={onSuccessOpenChange}
        defaultOpen={false}
      >
        <Dialog.Portal container={portalContainer ?? undefined}>
          <div data-cf-external>
            <Dialog.Body overlayZIndex={successZIndex - 1} zIndex={successZIndex}>
              <UpgradeCompleteScreenDialog subscriptionType={subscriptionType} />
            </Dialog.Body>
          </div>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  )
}
